import { Link, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { isProductEnabled, ROUTE_TO_PRODUCT } from "@/lib/productCatalog";
import { Shield, HeartPulse, HandHeart, Mountain, PackageOpen, ArrowRight, Home, Car, ExternalLink, Stethoscope, Briefcase, Calculator, Users2 } from "lucide-react";

const PRODUCTS = [
  { to: "/quota/nuovo", icon: Shield, title: "RC Economia Solidale", desc: "Responsabilità civile per Enti del Terzo Settore.", accent: "text-forest", bg: "bg-forest/10" },
  { to: "/quota/mrv", icon: HandHeart, title: "Tutela Multirischi del Volontariato", desc: "Protezione integrata per volontari e attività di volontariato.", accent: "text-sky-700", bg: "bg-sky-100" },
  { to: "/quota/infortuni", icon: HeartPulse, title: "Infortuni per l'Economia Solidale", desc: "Copertura infortuni per soci, volontari, clero e attività.", accent: "text-rose-700", bg: "bg-rose-100" },
  { to: "/quota/catnat", icon: Mountain, title: "CAT NAT Assimoco", desc: "Catastrofi naturali (terremoto, alluvione, frana) ex L. 213/2023.", accent: "text-amber-700", bg: "bg-amber-100", badge: "Solo Sardegna" },
  { to: "/quota/multirischi", icon: PackageOpen, title: "Tutela Multirischi Danni ai Beni", desc: "Incendio, Furto/Rapina ed Elettronica per ETS e Botteghe del Mondo.", accent: "text-indigo-700", bg: "bg-indigo-100" },
  { to: "/quota/casa", icon: Home, title: "Tutela Multirischi per l'Abitazione", desc: "Casa: Incendio, Furto, RC, Energie Rinnovabili, Tutela Legale, Pet, Assistenza.", accent: "text-emerald-700", bg: "bg-emerald-100", badge: "Nuovo" },
  { to: "/quota/cooperazione-salute", icon: Stethoscope, title: "Cooperazione Salute 4You", desc: "Piani sanitari Salute e Sostegno, single e famiglia, con detrazione 19%.", accent: "text-red-700", bg: "bg-red-100", badge: "Nuovo" },
  { to: "/quota/do", icon: Briefcase, title: "D&O Directors & Officers", desc: "RC degli amministratori e Tutela Legale: preventivo indicativo da inviare alla Direzione.", accent: "text-violet-800", bg: "bg-violet-100", badge: "Nuovo" },
  { to: "/formula-service", icon: Calculator, title: "Formula Service", desc: "Infortuni e Assistenza Eticapro per le persone seguite dal Partner.", accent: "text-teal-700", bg: "bg-teal-100" },
  { to: "/formula-self", icon: Users2, title: "Formula Self — Protezione Famiglia", desc: "Infortuni H24 per persone e famiglie, con link da inviare al cliente.", accent: "text-orange-700", bg: "bg-orange-100" },
  { href: "https://assietica.it/basta-la-targa", icon: Car, title: "Auto / RCA — Basta la Targa", desc: "Preventivo RCA sul sito Assietica: inserisci la targa e ricevi il preventivo.", accent: "text-slate-700", bg: "bg-slate-100", external: true },
];

export default function ProductHub() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const qs = params.toString() ? `?${params.toString()}` : "";
  const visible = PRODUCTS.filter((p) =>
    p.external ? isProductEnabled(user, "auto") : isProductEnabled(user, ROUTE_TO_PRODUCT[p.to]));

  const Inner = ({ p }) => (
    <Card className="group h-full rounded-xl border-border/60 p-5 transition-all hover:-translate-y-0.5 hover:border-forest/40 hover:shadow-md">
      <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${p.bg}`}>
        <p.icon className={`h-6 w-6 ${p.accent}`} />
      </div>
      <div className="mt-4 flex items-center gap-2">
        <h2 className="font-heading text-lg font-semibold leading-tight text-forest">{p.title}</h2>
        {p.badge && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">{p.badge}</span>}
      </div>
      <p className="mt-1.5 text-sm text-muted-foreground">{p.desc}</p>
      <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-terracotta group-hover:gap-2 transition-all">
        {p.external ? <>Vai al sito <ExternalLink className="h-4 w-4" /></> : <>Avvia preventivo <ArrowRight className="h-4 w-4" /></>}
      </span>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-semibold text-forest">Nuovo preventivo</h1>
        <p className="mt-1 text-muted-foreground">Scegli il prodotto assicurativo da quotare.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map((p) => (
          p.external ? (
            <a key={p.href} href={p.href} target="_blank" rel="noopener noreferrer" data-testid="hub-rca-basta-la-targa"><Inner p={p} /></a>
          ) : (
            <Link key={p.to} to={`${p.to}${qs}`} data-testid={`hub-${p.to.split("/").pop()}`}><Inner p={p} /></Link>
          )
        ))}
      </div>
    </div>
  );
}
